import { useFileDialog } from "../../../../hooks/use-file-dialog.js";
import CodeInput_default from "../../../components/code-input/CodeInput.vue.js";
import DataTable_default from "../../../components/data-table/DataTable.vue.js";
import DataTableHeader_default from "../../../components/data-table/DataTableHeader.vue.js";
import DataTableRow_default from "../../../components/data-table/DataTableRow.vue.js";
import { getFileName } from "../helpers/files.js";
import RequestBodyForm_default from "./RequestBodyForm.vue.js";
import { getStructuredBodyCodec } from "../helpers/structured-body-codec.js";
import RequestBodyStructured_default from "./RequestBodyStructured.vue.js";
import RequestBodyViewToggle_default from "./RequestBodyViewToggle.vue.js";
import CollapsibleSection_default from "../../../components/layout/CollapsibleSection.vue.js";
import { Fragment, computed, createBlock, createCommentVNode, createElementBlock, createElementVNode, createTextVNode, createVNode, defineComponent, openBlock, ref, toDisplayString, unref, watch, withCtx, withModifiers } from "vue";
import { ScalarButton } from "@scalar/components/button";
import { ScalarListbox } from "@scalar/components/listbox";
import { ScalarIconMagicWand } from "@scalar/icons";
import { objectEntries } from "@scalar/helpers/object/object-entries";
import { ScalarIcon } from "@scalar/components/icon";
import { ScalarIconButton } from "@scalar/components/icon-button";
import { getExampleFromBody, getSchemaExampleFromBody, getSelectedBodyContentType } from "@scalar/workspace-store/request-example";
import { parseMimeType } from "@scalar/helpers/http/mime-type";
import { unpackProxyObject } from "@scalar/workspace-store/helpers/unpack-proxy";
import { ScalarCodeBlockCopy } from "@scalar/components/code-block";
import { ScalarCopyBackdrop } from "@scalar/components/copy";
import { CONTENT_TYPES } from "@scalar/helpers/http/content-types";
import { isObject } from "@scalar/helpers/object/is-object";
import { resolve } from "@scalar/workspace-store/resolve";
import { isObjectSchema } from "@scalar/workspace-store/schemas/v3.2/strict/type-guards";
//#region src/v2/blocks/request-block/components/RequestBody.vue?vue&type=script&setup=true&lang.ts
var _hoisted_1 = { class: "flex items-center gap-1" };
var _hoisted_2 = {
	key: 0,
	class: "text-c-3 flex min-h-10 w-full items-center justify-center border-t p-2 text-sm"
};
var _hoisted_3 = {
	key: 1,
	class: "flex items-center justify-center overflow-hidden border-t p-1.5"
};
var _hoisted_4 = { class: "text-c-2 w-full max-w-full overflow-hidden rounded border px-1.5 py-1 text-xs whitespace-nowrap" };
var _hoisted_5 = {
	key: 1,
	class: "group/code-block relative border-t"
};
var RequestBody_vue_vue_type_script_setup_true_lang_default = /*@__PURE__*/ defineComponent({
	__name: "RequestBody",
	props: {
		requestBody: {},
		exampleKey: {},
		title: {},
		environment: {},
		requestBodyCompositionSelection: {},
		defaultView: { default: "raw" }
	},
	emits: [
		"update:contentType",
		"update:value",
		"update:formValue",
		"generate:example"
	],
	setup(__props, { emit: __emit }) {
		const emits = __emit;
		const contentTypeToLanguageMap = {
			"application/json": "json",
			"application/xml": "xml",
			"application/yaml": "yaml"
		};
		/** Selected content type with default */
		const selectedContentType = computed(() => getSelectedBodyContentType(__props.requestBody, __props.exampleKey) ?? "none");
		/** Keep the editor and copy label aligned with the selected content type. */
		const selectedLanguage = computed(() => contentTypeToLanguageMap[selectedContentType.value] ?? "plaintext");
		/**
		* Strips MIME parameters (such as `charset=utf-8`) for dropdown labels, aligned with how we treat
		* media types elsewhere (see api-reference `normalizeMimeType` / WHATWG essence).
		*/
		const contentTypeLabel = (raw) => parseMimeType(raw).essence;
		/**
		* Build the dropdown options for the content type selector.
		*
		* The list is composed of two groups:
		*   1. The built-in content types we ship friendly labels for (JSON, XML, Multipart Form, etc.).
		*   2. Any additional content types defined on the OpenAPI request body that are not in the built-in list
		*      (for example `text/csv`, `application/pdf`, vendor types like `application/vnd.api+json`, ...).
		*
		* The OpenAPI-defined extras are appended at the bottom so the well-known options stay on top, and
		* users can always pick the exact content type the operation actually accepts.
		*/
		const contentTypeOptions = computed(() => {
			const builtIn = objectEntries(CONTENT_TYPES).map(([id, label]) => ({
				id,
				label
			}));
			const extras = Object.keys(__props.requestBody?.content ?? {}).filter((type) => {
				if (type in CONTENT_TYPES) return false;
				return !(contentTypeLabel(type) in CONTENT_TYPES);
			}).map((type) => ({
				id: type,
				label: contentTypeLabel(type)
			}));
			return [...builtIn, ...extras];
		});
		const selectedContentTypeModel = computed({
			get: () => {
				const found = contentTypeOptions.value.find((it) => it.id === selectedContentType.value);
				if (found) return found;
				const essence = contentTypeLabel(selectedContentType.value);
				const friendly = CONTENT_TYPES[essence] ?? essence;
				return {
					id: selectedContentType.value,
					label: friendly
				};
			},
			set: (v) => {
				emits("update:contentType", { value: v.id });
			}
		});
		function handleFileUpload(callback) {
			const { open } = useFileDialog({
				onChange: (files) => {
					const file = files?.[0];
					if (file) callback(file);
				},
				multiple: false,
				accept: "*/*"
			});
			open();
		}
		/** Dereferenced example */
		const example = computed(() => __props.requestBody && getExampleFromBody(__props.requestBody, selectedContentType.value, __props.exampleKey, __props.requestBodyCompositionSelection));
		/** Convert the example value to a string for the code editor */
		const bodyValue = computed(() => {
			if (!example.value) return "";
			const value = example.value.value;
			if (typeof value === "string") return value;
			return JSON.stringify(value, null, 2);
		});
		/** Resolved schema for the request body */
		const bodySchema = computed(() => {
			return resolve.schema(__props.requestBody?.content?.[selectedContentType.value]?.schema);
		});
		/** Codec for structured (JSON/YAML) bodies, undefined for everything else */
		const structuredCodec = computed(() => selectedContentType.value === "none" ? void 0 : getStructuredBodyCodec(selectedContentType.value));
		/** Parsed body value for the form view — `ok: false` when the raw text is not parseable */
		const parsedBody = computed(() => {
			const codec = structuredCodec.value;
			if (!codec) return { ok: false };
			const raw = example.value?.value;
			if (raw === void 0 || raw === null || raw === "") return {
				ok: true,
				value: {}
			};
			if (typeof raw === "string") try {
				return {
					ok: true,
					value: codec.parse(raw)
				};
			} catch {
				return { ok: false };
			}
			return {
				ok: true,
				value: raw
			};
		});
		watch([
			() => __props.requestBody,
			() => __props.exampleKey,
			computed(() => JSON.stringify(__props.requestBodyCompositionSelection ?? {}))
		], ([, , selection], [previousRequestBody, previousExampleKey, previousSelection]) => {
			if (__props.requestBody !== previousRequestBody || __props.exampleKey !== previousExampleKey || !(selection !== previousSelection) || previousSelection === "{}" || Object.keys(__props.requestBodyCompositionSelection ?? {}).length === 0) return;
			const codec = structuredCodec.value;
			if (!__props.requestBody || !codec) return;
			const selectedValue = getSchemaExampleFromBody(__props.requestBody, selectedContentType.value, __props.requestBodyCompositionSelection);
			emits("update:value", {
				payload: selectedValue == null ? "" : codec.stringify(selectedValue),
				contentType: selectedContentType.value
			});
		});
		/** The form view only works on an object-shaped body root */
		const isFormViewAvailable = computed(() => parsedBody.value.ok && isObject(parsedBody.value.value));
		/**
		* Show the Form/Raw toggle for structured bodies that are (or per schema should be)
		* an object at the root. Non-object roots (arrays, primitives) stay raw-only.
		*/
		const showBodyViewToggle = computed(() => Boolean(structuredCodec.value) && (isFormViewAvailable.value || Boolean(bodySchema.value && isObjectSchema(bodySchema.value))));
		/** Selected body view, seeded from the document default (raw unless configured) */
		const bodyView = ref(__props.defaultView);
		watch(isFormViewAvailable, (ok) => {
			if (!ok) bodyView.value = "raw";
		});
		/**
		* Whether we can offer to generate a fresh example from the schema.
		*
		* Only structured (JSON/YAML) bodies qualify: they have a schema to generate from and a text
		* representation we can seed the new example with. Binary and form bodies have their own editors,
		* so the button would have nothing meaningful to write.
		*/
		const canGenerateExample = computed(() => Boolean(structuredCodec.value && bodySchema.value));
		return (_ctx, _cache) => {
			return openBlock(), createBlock(unref(CollapsibleSection_default), null, {
				title: withCtx(() => [createTextVNode(toDisplayString(__props.title), 1)]),
				default: withCtx(() => [createVNode(unref(DataTable_default), {
					columns: [""],
					presentational: ""
				}, {
					default: withCtx(() => [createVNode(unref(DataTableHeader_default), { class: "relative col-span-full flex h-8 cursor-pointer items-center justify-between border-r-0 !p-0" }, {
						default: withCtx(() => [createVNode(unref(ScalarListbox), {
							modelValue: selectedContentTypeModel.value,
							"onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => selectedContentTypeModel.value = $event),
							options: contentTypeOptions.value,
							teleport: ""
						}, {
							default: withCtx(() => [createVNode(unref(ScalarButton), {
								class: "text-c-2 hover:text-c-1 flex h-full w-fit gap-1.5 px-3 font-normal",
								fullWidth: "",
								variant: "ghost"
							}, {
								default: withCtx(() => [createElementVNode("span", null, toDisplayString(selectedContentTypeModel.value.label), 1), createVNode(unref(ScalarIcon), {
									icon: "ChevronDown",
									size: "md"
								})]),
								_: 1
							})]),
							_: 1
						}, 8, ["modelValue", "options"]), createElementVNode("div", _hoisted_1, [canGenerateExample.value ? (openBlock(), createBlock(unref(ScalarIconButton), {
							key: 0,
							"aria-label": "Generate example from schema",
							icon: unref(ScalarIconMagicWand),
							label: "Generate example from schema",
							size: "sm",
							tooltip: "",
							onClick: _cache[1] || (_cache[1] = withModifiers(($event) => emits("generate:example", { contentType: selectedContentType.value }), ["stop"]))
						}, null, 8, ["icon"])) : createCommentVNode("", true), showBodyViewToggle.value ? (openBlock(), createBlock(RequestBodyViewToggle_default, {
							key: 1,
							disabled: !isFormViewAvailable.value,
							modelValue: bodyView.value,
							"onUpdate:modelValue": _cache[2] || (_cache[2] = (v) => bodyView.value = v)
						}, null, 8, ["disabled", "modelValue"])) : createCommentVNode("", true)])]),
						_: 1
					}), createVNode(unref(DataTableRow_default), null, {
						default: withCtx(() => [selectedContentType.value === "none" ? (openBlock(), createElementBlock("div", _hoisted_2, [..._cache[8] || (_cache[8] = [createElementVNode("span", null, "No Body", -1)])])) : selectedContentType.value === "application/octet-stream" ? (openBlock(), createElementBlock("div", _hoisted_3, [unref(getFileName)(unref(unpackProxyObject)(example.value?.value)) !== void 0 ? (openBlock(), createElementBlock(Fragment, { key: 0 }, [createElementVNode("span", _hoisted_4, toDisplayString(unref(getFileName)(unref(unpackProxyObject)(example.value?.value))), 1), createVNode(unref(ScalarButton), {
							class: "bg-b-2 hover:bg-b-3 text-c-2 ml-1 border-0 shadow-none",
							size: "sm",
							variant: "outlined",
							onClick: _cache[3] || (_cache[3] = ($event) => emits("update:value", {
								payload: void 0,
								contentType: selectedContentType.value
							}))
						}, {
							default: withCtx(() => [..._cache[9] || (_cache[9] = [createTextVNode(" Delete ", -1)])]),
							_: 1
						})], 64)) : (openBlock(), createBlock(unref(ScalarButton), {
							key: 1,
							class: "bg-b-2 hover:bg-b-3 text-c-2 border-0 shadow-none",
							size: "sm",
							variant: "outlined",
							onClick: _cache[4] || (_cache[4] = () => handleFileUpload((file) => emits("update:value", {
								payload: file,
								contentType: selectedContentType.value
							})))
						}, {
							default: withCtx(() => [_cache[10] || (_cache[10] = createElementVNode("span", null, "Select File", -1)), createVNode(unref(ScalarIcon), {
								class: "ml-1",
								icon: "Upload",
								size: "xs",
								thickness: "2.5"
							})]),
							_: 1
						}))])) : selectedContentType.value === "multipart/form-data" || selectedContentType.value === "application/x-www-form-urlencoded" ? (openBlock(), createBlock(RequestBodyForm_default, {
							key: 2,
							bodySchema: bodySchema.value,
							environment: __props.environment,
							example: example.value,
							selectedContentType: selectedContentType.value,
							"onUpdate:formValue": _cache[5] || (_cache[5] = (payload) => emits("update:formValue", {
								payload,
								contentType: selectedContentType.value
							}))
						}, null, 8, [
							"bodySchema",
							"environment",
							"example",
							"selectedContentType"
						])) : (openBlock(), createElementBlock(Fragment, { key: 3 }, [bodyView.value === "form" && isFormViewAvailable.value ? (openBlock(), createBlock(RequestBodyStructured_default, {
							key: 0,
							bodySchema: bodySchema.value,
							contentType: selectedContentType.value,
							environment: __props.environment,
							parsedValue: parsedBody.value.value,
							"onUpdate:value": _cache[6] || (_cache[6] = (value) => emits("update:value", {
								payload: value,
								contentType: selectedContentType.value
							}))
						}, null, 8, [
							"bodySchema",
							"contentType",
							"environment",
							"parsedValue"
						])) : (openBlock(), createElementBlock("div", _hoisted_5, [createVNode(unref(CodeInput_default), {
							class: "px-3",
							content: "",
							environment: __props.environment,
							language: selectedLanguage.value,
							lineNumbers: "",
							lint: "",
							modelValue: bodyValue.value,
							withFakeData: "",
							"onUpdate:modelValue": _cache[7] || (_cache[7] = (value) => emits("update:value", {
								payload: value,
								contentType: selectedContentType.value
							}))
						}, null, 8, [
							"environment",
							"language",
							"modelValue"
						]), bodyValue.value ? (openBlock(), createBlock(unref(ScalarCodeBlockCopy), {
							key: 0,
							class: "absolute top-1.5 right-1.5",
							content: bodyValue.value,
							lang: selectedLanguage.value,
							showLang: ""
						}, {
							backdrop: withCtx(() => [createVNode(unref(ScalarCopyBackdrop), { class: "-top-1 -right-1.5" })]),
							_: 1
						}, 8, ["content", "lang"])) : createCommentVNode("", true)]))], 64))]),
						_: 1
					})]),
					_: 1
				})]),
				_: 1
			});
		};
	}
});
//#endregion
export { RequestBody_vue_vue_type_script_setup_true_lang_default as default };

//# sourceMappingURL=RequestBody.vue.script.js.map